import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';

import { formatDateByLocale } from '../../../../date/date-formatter';
import { TableCellLinkWrapper } from '../common/TableCellLinkWrapper';
import { SkillCell } from '../skills/SkillCell';
import { TableCellWrapper } from '../skills/TableCellWrapper';
import type { LearningOpportunitySearchRow } from './LearningOpportunitiesDataGrid';

export const useLearningOpportunitiesSearchColumns = (
    prioritySkillIds?: readonly string[],
): GridColDef<LearningOpportunitySearchRow>[] => {
    const {
        applicationState: { languageKey },
        translate,
    } = useContext(ApplicationContext);

    return [
        {
            disableColumnMenu: true,
            field: 'title',
            flex: 0.5,
            headerName: translate('pages.global-search.headlines.courses'),
            renderCell: ({ row }: GridRenderCellParams<LearningOpportunitySearchRow>) => {
                const title = row.title ?? '';
                const url = `/learning-opportunities/${row.id}`;

                return <TableCellLinkWrapper to={url}>{title}</TableCellLinkWrapper>;
            },
            resizable: true,
            sortable: true,
        },
        {
            disableColumnMenu: true,
            field: 'createdAt',
            flex: 0.5,
            headerName: translate('pages.global-search.headlines.created'),
            renderCell: ({ row }: GridRenderCellParams<LearningOpportunitySearchRow>) => {
                const formattedDate = formatDateByLocale({
                    dateStringToFormat: Maybe.of(row?.createdAt),
                    locale: languageKey,
                }).unwrapOr(null);

                return <TableCellWrapper>{formattedDate}</TableCellWrapper>;
            },
            resizable: false,
            sortable: true,
        },
        {
            disableColumnMenu: true,
            field: 'skills',
            flex: 1,
            headerName: translate('pages.global-search.headlines.trained-skills'),
            renderCell: ({ row }: GridRenderCellParams<LearningOpportunitySearchRow>) => (
                <SkillCell
                    defaultVisibleSkillsCount={1}
                    prioritySkillIds={prioritySkillIds}
                    skills={row.skills ?? []}
                />
            ),
            sortable: false,
        },
    ];
};
